/**
 * Where a page starts when you arrive at it — including when you arrive by
 * pressing Back.
 *
 * ## The gap this closes
 *
 * The shell scrolls in `<main>`, not the window, so neither the browser's own
 * scroll restoration nor the router's (both of which target the window) reaches
 * it. What the shell had instead was an unconditional scroll-to-top on every
 * path change, which is right for a link and wrong for the Back button:
 * measured on `/transactions`, scrolling 482px down the list, opening a
 * transaction and pressing Back landed the reader at row one every time. In a
 * tool whose whole loop is "scan a list → open one → come back → open the next",
 * that loses the reader's place on every single pass.
 *
 * ## The rule
 *
 * Three cases, and they are genuinely different:
 *
 * - **Same path, new search params.** Not an arrival at all — a tab strip or a
 *   chain selector rewriting the URL of the page you are already reading. Leave
 *   the scroll alone; yanking someone to the top when they pick a tab is its own
 *   bug, and it is why the reset was keyed on the path in the first place.
 * - **A history entry we have seen before.** Back or Forward. Restore what was
 *   there when it was left.
 * - **Anything else.** A fresh push. Start at the top.
 *
 * Keyed on the history entry, not on the URL: two visits to `/transactions` are
 * two places in the reader's history, and the second must not inherit where the
 * first was left.
 *
 * ## Why restoring needs more than one attempt
 *
 * Every page in this app is a lazy route whose content then loads asynchronously,
 * so at the moment of the location change `<main>` is a spinner a few hundred
 * pixels tall — `scrollTop = 482` on it silently clamps to 0, and the reader
 * lands at the top anyway, which is the bug the fix was for. {@link restoreScroll}
 * therefore keeps asking until the container is tall enough to honour the offset
 * or the deadline passes, and stops the moment the reader scrolls for themselves.
 */

/** The offsets we remember, keyed by history entry. */
export type ScrollMemory = Map<string, number>;

/**
 * The history entry's own identity, which is what makes "have I been here
 * before" answerable.
 *
 * TanStack stores a per-entry key in `location.state.__TSR_key` (the same field
 * its own scroll restoration keys on). Before the first navigation there is no
 * key yet, and `href` is the honest fallback: on a first paint there is nothing
 * remembered under it either way.
 */
export function scrollKeyOf(location: {
  href: string;
  state?: { __TSR_key?: string } | undefined;
}): string {
  return location.state?.__TSR_key || location.href;
}

export type ArrivalKind = "stay" | "restore" | "top";

/**
 * What should happen to the scroll position on this location change.
 *
 * Pure, so the three cases can be stated as facts rather than driven through a
 * router and a DOM.
 */
export function arrivalKind(input: {
  previousPathname: string | null;
  nextPathname: string;
  remembered: number | undefined;
}): ArrivalKind {
  if (input.previousPathname === input.nextPathname) return "stay";
  if (input.remembered !== undefined && input.remembered > 0) return "restore";
  return "top";
}

/** How long to keep trying to reach a remembered offset while a page loads. */
export const RESTORE_DEADLINE_MS = 3000;

export interface RestoreScrollOptions {
  element: HTMLElement;
  top: number;
  deadlineMs?: number;
  /** Injected in tests; defaults to `requestAnimationFrame` over `Date.now`. */
  now?: () => number;
  schedule?: (fn: () => void) => void;
}

/**
 * Scroll `element` to `top`, retrying while the page fills out.
 *
 * Returns a cancel function, which the caller MUST run when the location
 * changes again — a restore still retrying into the previous page's offset
 * would fight the next page's own arrival.
 *
 * Stops early on three conditions, all of them "the answer is already known":
 * the offset was reached, the container has finished growing past it (so the
 * clamp is the real end of the page and not a half-loaded one), or the reader
 * has scrolled somewhere themselves — at which point the remembered position is
 * no longer what they want.
 */
export function restoreScroll(options: RestoreScrollOptions): () => void {
  const { element, top } = options;
  const deadlineMs = options.deadlineMs ?? RESTORE_DEADLINE_MS;
  const now = options.now ?? (() => Date.now());
  const schedule =
    options.schedule ??
    ((fn: () => void) => {
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(fn);
      else setTimeout(fn, 16);
    });

  const started = now();
  let cancelled = false;
  // What we last set. A reader who scrolls during the retry window leaves
  // `scrollTop` somewhere we did not put it, which is how we notice.
  let lastSet: number | null = null;

  const attempt = (): void => {
    if (cancelled) return;
    if (lastSet !== null && Math.abs(element.scrollTop - lastSet) > 1) return;
    element.scrollTop = top;
    lastSet = element.scrollTop;
    if (Math.abs(element.scrollTop - top) <= 1) return;
    if (now() - started >= deadlineMs) return;
    schedule(attempt);
  };

  attempt();
  return () => {
    cancelled = true;
  };
}
